import { BarChart3, Users, TrendingUp, Clock } from 'lucide-react'
import type { Benefit } from '../types'

const Benefits = () => {
  const benefits: Benefit[] = [
    {
      id: 'data-driven',
      title: 'Data-Driven Results',
      description: 'Every strategy is backed by comprehensive analytics and real performance metrics. We track, measure, and optimize every campaign for maximum ROI.',
      icon: 'BarChart3'
    },
    {
      id: 'strategic-partnership',
      title: 'Strategic Partnership',
      description: 'We become an extension of your team, working closely with you to understand your goals and develop customized solutions that align with your business objectives.',
      icon: 'Users'
    },
    {
      id: 'scalable-solutions',
      title: 'Scalable Solutions',
      description: 'Our strategies grow with your business. From startups to enterprises, we provide flexible solutions that scale seamlessly as your needs evolve.',
      icon: 'TrendingUp'
    },
    {
      id: 'expert-support',
      title: '24/7 Expert Support',
      description: 'Get round-the-clock access to our team of digital marketing experts. We\'re always here to answer questions, provide insights, and optimize your campaigns.',
      icon: 'Clock'
    }
  ]

  const getIcon = (iconName: string) => {
    const icons = {
      BarChart3: BarChart3,
      Users: Users,
      TrendingUp: TrendingUp,
      Clock: Clock
    }
    const IconComponent = icons[iconName as keyof typeof icons]
    return IconComponent ? <IconComponent size={40} /> : <BarChart3 size={40} />
  }

  return (
    <section className="section-spacing bg-gray-50">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Why Choose Clybit Digital
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We don't just provide services – we deliver results. Here's what makes 
            us the trusted partner for businesses looking to accelerate their growth.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefits.map((benefit) => (
            <div
              key={benefit.id}
              className="text-center group"
            >
              {/* Icon */}
              <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <div className="text-primary group-hover:text-white transition-colors duration-300">
                  {getIcon(benefit.icon)}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                {benefit.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-20 bg-white rounded-2xl p-8 lg:p-12 shadow-sm">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Proven Track Record of Success
            </h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Numbers don't lie. Here's the measurable impact we've delivered for our clients.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">200+</div>
              <div className="text-gray-600 font-medium">Successful Projects</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">500%</div>
              <div className="text-gray-600 font-medium">Average ROI Increase</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">98%</div>
              <div className="text-gray-600 font-medium">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl lg:text-5xl font-bold text-primary mb-2">5+</div>
              <div className="text-gray-600 font-medium">Years Experience</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Benefits